// src/services/mercadopago.js
const PUBLIC_KEY = 'TEST-f8557a31-daf6-40f9-b9ef-95990878ee8e';

// API gratuita para obtener TRM (Tasa Representativa del Mercado) - Sin tokens, sin límites estrictos
const TRM_API_URL = 'https://open.er-api.com/v6/latest/USD';

// Configuración de planes con precios en COP y USD
const planesConfig = {
  starter: {
    title: 'STRATIUM GLOBAL AI - Plan Starter',
    priceCOP: 0,  // ✅ GRATIS - 15 días de prueba
    priceUSD: 0,  // ✅ GRATIS - 15 días de prueba
    description: '10 escaneos/mes, ingreso manual, dashboard básico, alertas de riesgo'
  },
  pro: {
    title: 'STRATIUM GLOBAL AI - Plan Pro',
    priceCOP: 79900,
    priceUSD: 29.99,
    description: '30 escaneos/mes, reportes PDF, exportar CSV, comparativas, punto de equilibrio, rotación de inventario'
  },
  business: {
    title: 'STRATIUM GLOBAL AI - Plan Business',
    priceCOP: 199900,
    priceUSD: 79.99,
    description: '120 escaneos/mes, auditoría forense de gastos, detección de sobrecostos, 3 usuarios'
  },
  elite: {
    title: 'STRATIUM GLOBAL AI - Plan Elite',
    priceCOP: 499900,
    priceUSD: 199.99,
    description: '300 escaneos/mes, radar de quiebra, alertas WhatsApp, certificado QR, 10 usuarios'
  }
};

// Función auxiliar para obtener la TRM del día desde API gratuita
const obtenerTRM = async () => {
  try {
    const response = await fetch(TRM_API_URL);
    if (!response.ok) {
      console.warn('⚠️ No se pudo obtener TRM de API externa, usando tasa de respaldo: 4000 COP/USD');
      return 4000; // Tasa de respaldo en caso de fallo
    }
    const data = await response.json();
    // La API devuelve rates.COP para la tasa USD a COP
    const trm = data?.rates?.COP;
    if (!trm || typeof trm !== 'number') {
      console.warn('⚠️ TRM inválida en respuesta, usando tasa de respaldo: 4000 COP/USD');
      return 4000;
    }
    return trm;
  } catch (error) {
    console.error('❌ Error obteniendo TRM:', error);
    return 4000; // Tasa de respaldo en caso de error
  }
};

// Función para crear preferencia de suscripción
export const crearPreferenciaSuscripcion = async (plan, userEmail, userId, moneda) => {
  // ✅ MANEJO ESPECIAL PARA PLAN STARTER (GRATIS)
  if (plan === 'starter') {
    return '/dashboard?status=success_starter';
  }
  
  const planData = planesConfig[plan];
  if (!planData) throw new Error('Plan no válido');
  
  const esCOP = moneda?.mostrarCOP !== undefined ? moneda.mostrarCOP : true;
  
  let price;
  let currency;
  let title;
  
  if (esCOP) {
    // Usuario colombiano: usar precio en COP directamente
    price = planData.priceCOP;
    currency = 'COP';
    title = `${planData.title} - $${price.toLocaleString('es-CO')} COP/mes`;
  } else {
    // ✅ Usuario internacional: convertir USD a COP usando TRM del día
    const trm = await obtenerTRM();
    // Calcular precio en COP redondeado al entero más cercano
    price = Math.round(planData.priceUSD * trm);
    // ⚠️ Mercado Pago Colombia SOLO acepta 'COP' como moneda
    currency = 'COP';
    // Mostrar transparencia: precio original en USD en el título
    const precioUSDFormateado = planData.priceUSD.toFixed(2);
    title = `${planData.title} - $${precioUSDFormateado} USD/mes (equiv. $${price.toLocaleString('es-CO')} COP)`;
  }

  const response = await fetch('/api/crear-preferencia', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      plan: plan,
      title: title,
      price: price,
      currency: currency,  // Siempre 'COP' para Mercado Pago Colombia
      description: planData.description,
      userEmail: userEmail,
      userId: userId,
      // Metadatos adicionales para trazabilidad
      metadata: {
        precioOriginalUSD: esCOP ? null : planData.priceUSD,
        trmAplicada: esCOP ? null : await obtenerTRM(),
        esUsuarioInternacional: !esCOP
      }
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Error ${response.status} al crear preferencia`);
  }

  const data = await response.json();
  return data.init_point;
};

// Función para verificar el estado de un pago
export const verificarPago = async (paymentId) => {
  const response = await fetch(`/api/verificar-pago?payment_id=${paymentId}`);
  const data = await response.json();
  return data;
};

// Función para crear preferencia de paquete de escaneos
export const crearPreferenciaPaquete = async (paquete, userEmail, userId, moneda) => {
  const paquetesConfig = {
    basico: { escaneos: 10, priceCOP: 19900, priceUSD: 9.99, title: 'Paquete Básico' },
    frecuente: { escaneos: 30, priceCOP: 49900, priceUSD: 19.99, title: 'Paquete Frecuente' },
    profesional: { escaneos: 100, priceCOP: 99900, priceUSD: 39.99, title: 'Paquete Profesional' },
    corporativo: { escaneos: 300, priceCOP: 199900, priceUSD: 79.99, title: 'Paquete Corporativo' }
  };
  
  const paqueteData = paquetesConfig[paquete];
  if (!paqueteData) throw new Error('Paquete no válido');
  
  const esCOP = moneda?.mostrarCOP !== undefined ? moneda.mostrarCOP : true;
  
  let price;
  let currency;
  let title;
  
  if (esCOP) {
    // Usuario colombiano: usar precio en COP directamente
    price = paqueteData.priceCOP;
    currency = 'COP';
    title = `${paqueteData.title} - ${paqueteData.escaneos} escaneos - $${price.toLocaleString('es-CO')} COP`;
  } else {
    // ✅ Usuario internacional: convertir USD a COP usando TRM del día
    const trm = await obtenerTRM();
    // Calcular precio en COP redondeado al entero más cercano
    price = Math.round(paqueteData.priceUSD * trm);
    // ⚠️ Mercado Pago Colombia SOLO acepta 'COP' como moneda
    currency = 'COP';
    // Mostrar transparencia: precio original en USD en el título
    const precioUSDFormateado = paqueteData.priceUSD.toFixed(2);
    title = `${paqueteData.title} - ${paqueteData.escaneos} escaneos - $${precioUSDFormateado} USD (equiv. $${price.toLocaleString('es-CO')} COP)`;
  }

  const response = await fetch('/api/crear-preferencia-paquete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      paquete: paquete,
      title: title,
      price: price,
      currency: currency,  // Siempre 'COP' para Mercado Pago Colombia
      escaneos: paqueteData.escaneos,
      userEmail: userEmail,
      userId: userId,
      // Metadatos adicionales para trazabilidad
      metadata: {
        precioOriginalUSD: esCOP ? null : paqueteData.priceUSD,
        trmAplicada: esCOP ? null : await obtenerTRM(),
        esUsuarioInternacional: !esCOP
      }
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Error ${response.status} al crear preferencia de paquete`);
  }

  const data = await response.json();
  return data.init_point;
};

// ✅ Exportaciones individuales (mantener compatibilidad con imports existentes)
export { PUBLIC_KEY };

// ✅ Exportación por defecto que agrupa todas las funciones
// Esto permite: import CheckoutMercadoPago from './mercadopago'
// Y también: import { PUBLIC_KEY, crearPreferenciaSuscripcion } from './mercadopago'
const CheckoutMercadoPago = {
  PUBLIC_KEY,
  crearPreferenciaSuscripcion,
  verificarPago,
  crearPreferenciaPaquete
};

export default CheckoutMercadoPago;

